// Button Component
// =================
// Feature #291: Updated with refined styling - elegant transitions, design-line accents, 12px border-radius

import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  disabled,
  className = '',
  children,
  ...props
}) => {
  // Feature #281: Explicitly set font-sans for consistent Inter typography
  // Feature #291: Updated with 12px border-radius (rounded-md), elegant transitions, refined focus states
  // Focus ring uses softer offset and opacity for less harsh visual feedback
  const baseStyles =
    'inline-flex items-center justify-center font-sans font-medium rounded-md transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-sm active:scale-[0.98] active:shadow-none';

  // Feature #291: Refined variants with elegant hover states and design-line accents
  // Primary variant uses design-line-blue for refined hover state
  // Accent variant uses design-line-orange for warm emphasis
  const variants = {
    primary:
      'bg-primary-600 text-white hover:bg-design-line-blue hover:shadow-md focus-visible:ring-design-line-blue/50 dark:bg-primary-600 dark:hover:bg-design-line-blue',
    secondary:
      'bg-secondary-600 text-white hover:bg-secondary-500 hover:shadow-md focus-visible:ring-secondary-500/50',
    accent:
      'bg-accent-500 text-white hover:bg-design-line-orange hover:shadow-md focus-visible:ring-design-line-orange/50',
    outline:
      'border-2 border-primary-500 text-primary-600 hover:bg-primary-50 hover:border-design-line-blue hover:text-design-line-blue focus-visible:ring-design-line-blue/50 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20 dark:hover:border-design-line-blue dark:hover:text-design-line-blue',
    ghost:
      'text-neutral-dark hover:bg-gray-100 hover:text-design-line-blue focus-visible:ring-gray-400/50 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-design-line-blue',
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2.5 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  const widthStyles = fullWidth ? 'w-full' : '';

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${widthStyles} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Indlaeser...
        </>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
